import { useState, useEffect, useRef, useCallback } from 'react';

import { useActions } from '../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../shared/common/hooks/useTypedSelector';
import { useTimerCleanup } from '../../../shared/common/hooks/useTimerCleanup';
import { DashboardComponent } from '../types';

interface UseDashboardFiltersOptions {
  editBlockVisible: boolean;
  addBlockVisible: boolean;
}

/**
 * Хук для управления фильтрами дашборда.
 *
 * Управляет:
 * - Синхронизацией фильтров между модалками и основным состоянием
 * - Кнопкой «Показать» при изменении глобальных параметров
 * - Обновлением данных всех блоков (двухфазный сброс fetching)
 */
export const useDashboardFilters = (
  dashboardComponents: DashboardComponent[],
  options: UseDashboardFiltersOptions
) => {
  const { editBlockVisible, addBlockVisible } = options;
  const { safeSetTimeout } = useTimerCleanup();

  const { filters } = useTypedSelector((state) => state.filter);
  const period = useTypedSelector((state) => state.period);
  const { detailsDashboard } = useTypedSelector((state) => state.details);
  const { attribution, type_applications } = useTypedSelector(
    (state) => state.attributes.attributesDashboard
  );

  const {
    setFilters,
    setMetaTableFetching,
    setMetaChartFetching,
  } = useActions();

  // ==================== State ====================

  const [dashboardFilters, setDashboardFilters] = useState(filters);
  const [fetchDashboardDataButtonVisible, setFetchDashboardDataButtonVisible] = useState(true);

  // ==================== Refs ====================

  const isMountRef = useRef(true);
  const prevGlobalSettingsRef = useRef({
    filters: [] as any[],
    attribution: '',
    type_applications: '',
    period: {} as any,
    detailsDashboard: '',
  });

  const isAnyModalOpen = editBlockVisible || addBlockVisible;

  // ==================== Эффекты ====================

  /**
   * Синхронизация фильтров при открытии/закрытии модалок.
   * При открытии — сохраняем текущие фильтры.
   * При закрытии — восстанавливаем сохранённые фильтры.
   */
  useEffect(() => {
    if (isAnyModalOpen) {
      setDashboardFilters(filters);
      return;
    }
    if (!isAnyModalOpen) {
      setFilters(dashboardFilters);
    }
  }, [editBlockVisible, addBlockVisible]);

  /**
   * Обновление dashboardFilters при изменении filters (вне модалок).
   */
  useEffect(() => {
    if (dashboardFilters?.length !== filters?.length && !isAnyModalOpen) {
      setDashboardFilters(filters);
    }
  }, [filters]);

  /**
   * Показ кнопки «Показать» при изменении глобальных параметров.
   * fetchDashboardDataButtonVisible = true  → кнопка скрыта (данные актуальны)
   * fetchDashboardDataButtonVisible = false → кнопка показана (нужно обновить)
   */
  useEffect(() => {
    if (isMountRef.current || isAnyModalOpen) return;

    const prev = prevGlobalSettingsRef.current;
    const hasGlobalChanges =
      prev.filters !== filters ||
      prev.attribution !== attribution ||
      prev.type_applications !== type_applications ||
      prev.period !== period ||
      prev.detailsDashboard !== detailsDashboard;

    prevGlobalSettingsRef.current = {
      filters,
      attribution,
      type_applications,
      period,
      detailsDashboard,
    };

    if (hasGlobalChanges) {
      setFetchDashboardDataButtonVisible(false);
    }
  }, [
    filters,
    attribution,
    type_applications,
    period,
    detailsDashboard,
    editBlockVisible,
    addBlockVisible,
  ]);

  // ==================== Обработчики ====================

  /**
   * Обновление данных всех блоков дашборда.
   * Использует двухфазный сброс: false → safeSetTimeout → true
   * (необходим из-за того что в React 17 dispatch-ы не батчатся).
   */
  const handleUpdateDashboardData = useCallback(() => {
    setFetchDashboardDataButtonVisible(true);

    dashboardComponents.forEach((item) => {
      switch (item.componentType) {
        case 'chart':
          setMetaChartFetching({ value: false, blockId: item.data.blockId });
          // Также сбрасываем table fetching для chart-блоков (pie/bar/treemap зависят от tableData)
          setMetaTableFetching({ value: false, blockId: item.data.blockId });
          break;
        case 'table':
        case 'number':
          setMetaTableFetching({ value: false, blockId: item.data.blockId });
          break;
      }
    });

    safeSetTimeout(() => {
      dashboardComponents.forEach((item) => {
        switch (item.componentType) {
          case 'chart':
            setMetaChartFetching({ value: true, blockId: item.data.blockId });
            // Также триггерим table fetching для chart-блоков (pie/bar/treemap зависят от tableData)
            setMetaTableFetching({ value: true, blockId: item.data.blockId });
            break;
          case 'table':
          case 'number':
            setMetaTableFetching({ value: true, blockId: item.data.blockId });
            break;
        }
      });
    }, 0);
  }, [dashboardComponents, safeSetTimeout]);

  return {
    dashboardFilters,
    fetchDashboardDataButtonVisible,
    setFetchDashboardDataButtonVisible,
    handleUpdateDashboardData,
    isMountRef,
  };
};

export default useDashboardFilters;
